import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CCollapse } from "@coreui/react";
import { toast, ToastContainer } from "react-toastify";
import generatePDF from "react-to-pdf";
import DashboardContent from "../../components/DashboardContent";
import RedirectHandler from "../../components/RedirectHandler";
import ScsRenderHistoryDropdown from "../../components/ScsRenderHistoryDropdown";
import useCsr from "../../hooks/useCsr";
import useScsRender from "../../hooks/useScsRender";
import { usePermissions } from "../../contexts/PermissionsContext";
import { NO_PERMISSION_TITLE, PERMISSIONS } from "../../utils/permissions";
import { CsrAPI } from "../../apis/CsrAPI";
import { setScsSummaryName } from "../../store/slices/scsDataSlice";
import { FILES_STATUS, SCS_STATUS } from "../../utils/Constants";
import { ReactComponent as CsrComplete } from "../../assets/images/icons/csr-complete.svg";
import { ReactComponent as CsrPending } from "../../assets/images/icons/csr-pending.svg";
import { ReactComponent as CsrWarning } from "../../assets/images/icons/csr-warning.svg";
import { ReactComponent as CsrError } from "../../assets/images/icons/file-error.svg";
import { ReactComponent as CsrGeneratePending } from "../../assets/images/icons/csr-generate-pending.svg";
import { ReactComponent as CheckCircle } from "../../assets/images/icons/check-circle.svg";
import { ReactComponent as CsrDownload } from "../../assets/images/icons/download-white.svg";
import { ReactComponent as CsrDownloadBlue } from "../../assets/images/icons/download-blue.svg";
import { ReactComponent as InfoUnfilled } from "../../assets/images/icons/info-unfilled.svg";
import { ReactComponent as ArrowRight } from "../../assets/images/icons/arrow-right.svg";

// Renders the status icon for a subsection based on its user_status.
const StatusIcon = ({ status }) => {
  if (status === "SUCCESS") return <CsrComplete />;
  if (status === "WARNING") return <CsrWarning />;
  return <CsrError />;
};

// Stable key for a group within a section — used to track accordion open state.
const groupKeyFor = (sectionIndex, groupIndex) =>
  `section-${sectionIndex}-group-${groupIndex}`;

const SCSFile = () => {
  const targetRef = useRef(null);
  const { studyId } = useParams();
  const dispatch = useDispatch();
  const { wrongEnvironment, scsFilesStatus, scsDocumentsList } = useCsr();
  const summaryName = useSelector(state => state.scsData.summaryName);
  const { hasPermission } = usePermissions();

  // scs:generation:create covers starting a render ("Generate SCS file" and
  // "Generate again"), scs:downloads:read downloading the rendered document —
  // both the latest one and any earlier version in the history dropdown.
  const canGenerateScs = hasPermission(PERMISSIONS.scsGenerationCreate);
  const canDownloadScs = hasPermission(PERMISSIONS.scsDownloadsRead);

  const [showRedirect, setShowRedirect] = useState(false);

  const {
    mainStatus,
    lastGeneratedAt,
    trafficLights,
    downloading,
    versionsToken,
    generate,
    download,
  } = useScsRender(studyId, {
    onForbidden: () => setShowRedirect(true),
  });

  // Landing straight on this page (deep link or refresh) skips the summary page
  // that normally seeds the name, so fetch it here when the store has none —
  // the header and the download filenames both need it.
  useEffect(() => {
    if (!studyId || summaryName) return;

    let active = true;
    CsrAPI.getSafetySummary({ summaryId: studyId })
      .then(data => {
        if (!active) return;
        const name = data?.summaryItem?.summaryName || "";
        if (name) {
          dispatch(
            setScsSummaryName({ summaryId: studyId, summaryName: name })
          );
        }
      })
      .catch(error => {
        if (active && error.response?.status === 403) {
          setShowRedirect(true);
        }
      });

    return () => {
      active = false;
    };
  }, [studyId, summaryName, dispatch]);

  // An SCS section can either carry `subsections` directly or be split into
  // user-defined `groups`, each holding its own `subsections`. Every group's
  // accordion starts expanded, so seed the open set from the incoming report.
  const [openGroups, setOpenGroups] = useState(new Set());

  useEffect(() => {
    const keys = new Set();
    trafficLights.forEach((section, sectionIndex) => {
      if (Array.isArray(section.groups)) {
        section.groups.forEach((_, groupIndex) => {
          keys.add(groupKeyFor(sectionIndex, groupIndex));
        });
      }
    });
    setOpenGroups(keys);
  }, [trafficLights]);

  const toggleGroup = useCallback(key => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const hasFiles = Object.values(scsDocumentsList).some(list => list.length);
  // Same precondition the backend enforces with a 409: nothing to render from
  // until at least one file has been uploaded and processed.
  const canGenerate = scsFilesStatus === FILES_STATUS.ready && hasFiles;
  const fileBaseName = summaryName || `SCS-${studyId}`;

  const toPDF = () => {
    generatePDF(targetRef, {
      filename: "Traffic Light Status.pdf",
      page: {
        // margin is in MM, default is Margin.NONE = 0
        margin: {
          top: 10,
          right: 20,
          bottom: 10,
          left: 30,
        },
        // default is 'A4'
        format: "A4",
        // default is 'portrait'
        orientation: "portrait",
      },
    });
  };

  const handleGenerateScs = event => {
    event.preventDefault();
    if (!canGenerateScs) return;

    generate();
  };

  const handleDownloadScs = event => {
    event.preventDefault();
    if (!canDownloadScs) return;

    download(`${fileBaseName}.docx`);
  };

  const handleDownloadTrafficLightsPDF = event => {
    event.preventDefault();
    if (!trafficLights.length) {
      toast.info(
        "Generate the SCS first to get a traffic light status report."
      );
      return;
    }
    toPDF();
  };

  // Render a single subsection row — identical markup to the CSR file page so
  // the look and feel matches exactly.
  const renderSubsection = useCallback((subSection, key, fallbackLabel) => {
    return (
      <div key={key} className={"csr-file__info-component"}>
        <div className={"csr-file__info-component__left"}>
          <span className={"csr-file__info-component__status"}>
            <StatusIcon status={subSection.user_status} />
          </span>
          <span className={"csr-file__info-component__label"}>
            {subSection.subsection ? subSection.subsection : fallbackLabel}
          </span>
        </div>
        <div className={"csr-file__info-component__rightContainer"}>
          <span className={"csr-file__info-component__messages"}>
            {(subSection.user_messages || []).map((message, i) => (
              <p key={`${key}-message-${i}`}>{message}</p>
            ))}
          </span>
        </div>
      </div>
    );
  }, []);

  const renderSection = useCallback(
    (section, sectionIndex) => {
      const sectionTitle = section.name;
      const sectionKey = `section-${sectionIndex}`;

      return (
        <div key={sectionKey} className={"scs-file__section"}>
          <span className={"csr-file__info-component__section-label"}>
            {sectionTitle}
          </span>

          {Array.isArray(section.groups) ? (
            // Grouped section: each user-defined group is a collapsible
            // accordion item, expanded by default.
            <div className={"accordion scs-file__group-accordion"}>
              {section.groups.map((group, groupIndex) => {
                const key = groupKeyFor(sectionIndex, groupIndex);
                const isOpen = openGroups.has(key);
                return (
                  <div key={key} className={"accordion-item"}>
                    <div className={"accordion-header"}>
                      <button
                        type="button"
                        className={`accordion-button${
                          isOpen ? "" : " collapsed"
                        }`}
                        onClick={() => toggleGroup(key)}>
                        {group.name}
                      </button>
                    </div>
                    <CCollapse
                      visible={isOpen}
                      className={"accordion-collapse"}>
                      <div className={"accordion-body"}>
                        <div className={"csr-file__info-components"}>
                          {(group.subsections || []).map(
                            (subSection, subIndex) =>
                              renderSubsection(
                                subSection,
                                `${key}-subsection-${subIndex}`,
                                sectionTitle
                              )
                          )}
                        </div>
                      </div>
                    </CCollapse>
                  </div>
                );
              })}
            </div>
          ) : (
            Array.isArray(section.subsections) &&
            section.subsections.map((subSection, subIndex) =>
              renderSubsection(
                subSection,
                `${sectionKey}-subsection-${subIndex}`,
                sectionTitle
              )
            )
          )}
        </div>
      );
    },
    [openGroups, toggleGroup, renderSubsection]
  );

  const PageContent = () => {
    return (
      <div className={"csr-file csr-file__wrapper"}>
        {/* Summary Name and Status Section */}
        <div className={"csr-file__study-header"}>
          <div className={"csr-file__study-name"}>{summaryName}</div>
        </div>

        <div className="csr-file__status-container">
          <div className={"csr-file__status-info"}>
            <InfoUnfilled className={"csr-file__status-icon"} />
            <span>Status: {mainStatus}</span>
          </div>

          {/* Last Generated Section */}
          {mainStatus === SCS_STATUS.ready && lastGeneratedAt && (
            <div className={"csr-file__last-generated"}>
              <CheckCircle className={"csr-file__check-icon"} />
              <span>Last generated - {lastGeneratedAt}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={"csr-file__action-buttons"}>
          {mainStatus === SCS_STATUS.ready ? (
            <>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow csr-file__download-button"
                }
                onClick={handleDownloadScs}
                disabled={downloading || !canDownloadScs}
                title={canDownloadScs ? undefined : NO_PERMISSION_TITLE}>
                <CsrDownload /> Download SCS
              </button>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow csr-file__download-button"
                }
                onClick={handleGenerateScs}
                disabled={!canGenerate || !canGenerateScs}
                title={canGenerateScs ? undefined : NO_PERMISSION_TITLE}>
                Generate again <ArrowRight />
              </button>
            </>
          ) : mainStatus === SCS_STATUS.generating ? (
            <>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow csr-file__download-button"
                }
                disabled={true}>
                Download SCS <CsrDownload />
              </button>
              <div className={"csr-file__generate-component"}>
                <div className={"csr-file__generate-component__left"}>
                  <span className={"csr-file__generate-component__status"}>
                    <CsrGeneratePending />
                  </span>
                  <span className={"csr-file__generate-component__label"}>
                    Generating SCS
                  </span>
                </div>
              </div>
            </>
          ) : (
            <button
              className={
                "input-submit input-submit__primary input-submit__small csr-file__generate-button"
              }
              onClick={handleGenerateScs}
              disabled={!canGenerate || !canGenerateScs}
              title={canGenerateScs ? undefined : NO_PERMISSION_TITLE}>
              Generate SCS file
            </button>
          )}
        </div>

        {/* SCS Content Checklist Card */}
        <div className={"csr-file__card"}>
          <div className={"csr-file__info-components"}>
            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  {summaryName ? <CsrComplete /> : <CsrPending />}
                </span>
                <span className={"csr-file__info-component__label"}>
                  Summary Name
                </span>
              </div>
              <div className={"csr-file__info-component__right"}></div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  {scsFilesStatus === FILES_STATUS.warning ? (
                    <CsrWarning />
                  ) : canGenerate ? (
                    <CsrComplete />
                  ) : (
                    <CsrPending />
                  )}
                </span>
                <span className={"csr-file__info-component__label"}>Files</span>
              </div>
              <div className={"csr-file__info-component__right"}>
                <Link
                  to={"/scs/" + studyId + "/file-upload"}
                  className={"csr-file__info-component__action"}>
                  Edit
                </Link>
              </div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  <CsrComplete />
                </span>
                <span className={"csr-file__info-component__label"}>
                  Wizard questions
                </span>
              </div>
              <div className={"csr-file__info-component__right"}>
                <Link
                  to={"/scs/" + studyId + "/description-of-studies"}
                  className={"csr-file__info-component__action"}>
                  Edit
                </Link>
              </div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  {trafficLights.length ? <CsrComplete /> : <CsrPending />}
                </span>
                <span className={"csr-file__info-component__label"}>
                  Traffic light status
                </span>
              </div>
              <div className={"csr-file__info-component__right"}>
                <button
                  className={
                    "input-submit input-submit__primary input-submit__downloadButtonGroup input-submit__pdf"
                  }
                  onClick={handleDownloadTrafficLightsPDF}
                  disabled={!trafficLights.length}>
                  <CsrDownloadBlue /> Download PDF
                </button>
              </div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  <CsrComplete />
                </span>
                <span className={"csr-file__info-component__label"}>
                  History
                </span>
              </div>
              <div className={"csr-file__info-component__right"}>
                <ScsRenderHistoryDropdown
                  summaryId={studyId}
                  fileBaseName={fileBaseName}
                  refreshToken={versionsToken}
                  disabled={!canDownloadScs}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Traffic lights display grouped by section (and group where present) */}
        {trafficLights.length > 0 && (
          <div className={"csr-file__card csr-file__traffic-lights-card"}>
            <div className={"csr-file__info-components"} ref={targetRef}>
              {trafficLights.map(renderSection)}
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Generate & Download SCS"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Data Mapping",
        buttonGoToAction: `/scs/${studyId}/data-mapping`,
      }}
      nextButton={false}
    />
  );
};

export default SCSFile;
